import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { RootComponent } from './components/root/root.component';
import { AuthenticationComponent } from './components/authentication/authentication.component';
import { AuthenticationGuard } from 'src/app/core/guards/authentication.guard';
import { UsersComponent } from './components/users/users.component';
import { UserEditComponent } from './components/user-edit/user-edit.component';
import { TransactionsComponent } from './components/transactions/transactions.component';
import { FeeComponent } from './components/fee/fee.component';
import { ScopeGuard } from 'src/app/core/guards/scope.guard';
import { TwoFactorAuthenticationComponent } from 'src/app/feature/components/two-factor-authentication/two-factor-authentication.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { PublicNotificationComponent } from 'src/app/shared/components/public-notification/public-notification.component';
import { PrivateNotificationComponent } from 'src/app/shared/components/private-notification/private-notification.component';
import { WithdrawModule } from './modules/withdraw/withdraw.module';

const routes: Routes = [
  {
    path: 'dashboard',
    component: RootComponent,
    canActivate: [AuthenticationGuard],
    children: [
      {
        path: '',
        component: WelcomeComponent
      },
      {
        path: 'user',
        component: UsersComponent,
        canActivate: [ScopeGuard],
        data: {
          scopes: ['admin:view:users'],
          checkType: 'AND'
        }
      },
      {
        path: 'block-withdraw',
        loadChildren: () =>
          import('./modules/block-withdraw/block-withdraw.module').then(
            (m) => m.BlockWithdrawModule
          ),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:view:withdraw-block-status',
            'admin:add:withdraw-block-status',
            'admin:delete:withdraw-block-status'],
          checkType: 'AND'
        }
      },
      {
        path: 'support',
        loadChildren: () =>
          import('./modules/support-request/support-requests.module').then(
            (m) => m.SupportRequestsModule
          ),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:view:support-requests'],
          checkType: 'AND'
        }
      },
      {
        path: 'partnership-pool',
        loadChildren: () =>
          import('./modules/partnership-pool/partnership-pool.module').then(
            (m) => m.PartnershipPoolModule
          ),
        canActivate: [ScopeGuard],
        data: {
          scopes: ['admin:view:user:transactions'],
          checkType: 'AND'
        }
      },
      {
        path: 'two-factor-authentication',
        component: TwoFactorAuthenticationComponent
      },
      {
        path: 'transactions',
        component: TransactionsComponent,
        canActivate: [ScopeGuard],
        data: {
          scopes: ['admin:view:user:transactions'],
          checkType: 'AND'
        }
      },
      {
        path: 'user/:id',
        component: UserEditComponent,
        canActivate: [ScopeGuard],
        data: {
          scopes: [
            'admin:edit:user',
            'admin:view:user:transactions',
            'admin:view:users:ibans',
            'admin:view:orders:fee',
            'admin:view:users:credit_cards'
          ],
          checkType: 'OR'
        }
      },
      {
        path: 'fee',
        component: FeeComponent,
        canActivate: [ScopeGuard],
        data: {
          scopes: ['admin:view:orders:fee'],
          checkType: 'AND'
        }
      },
      {
        path: 'identification',
        loadChildren: () =>
          import('./modules/identification/identification.module').then(
            (m) => m.IdentificationModule
          ),
        canLoad: [ScopeGuard],
        data: {
          scopes: [
            'admin:view:users:identity_photo_verification',
            'admin:view:users:personal_information_verification',
            'admin:view:users:address_verification'
          ],
          checkType: 'OR'
        }
      },
      {
        path: 'reconciliation',
        loadChildren: () =>
          import('./modules/reconciliation/reconciliation.module').then(
            (m) => m.ReconciliationModule
          )
      },
      {
        path: 'market-orders',
        loadChildren: () =>
          import('./modules/market-orders/market-orders.module').then((m) => m.MarketOrdersModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: [
            'admin:view:order'
          ],
          checkType: 'AND'
        }
      },
      {
        path: 'robot',
        loadChildren: () => import('./modules/robot/robot.module').then((m) => m.RobotModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:view:robot'],
          checkType: 'AND'
        }
      },
      {
        path: 'withdraw',
        loadChildren: () => WithdrawModule,
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:view:users:withdraw'],
          checkType: 'AND'
        }
      },
      {
        path: 'report',
        loadChildren: () => import('./modules/report/report.module').then((m) => m.ReportModule)
      },
      {
        path: 'transfer',
        loadChildren: () =>
          import('./modules/transfer-money/transfer-money.module').then(
            (m) => m.TransferMoneyModule
          ),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:wallet:transfer', 'admin:view:wallet:transferv2'],
          checkType: 'OR'
        }
      },
      {
        path: 'zeepay',
        loadChildren: () => import('./modules/zeepay/zeepay.module').then((m) => m.ZeepayModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:view:zeepay:deposit', 'admin:view:zeepay:balance'],
          checkType: 'OR'
        }
      },
      {
        path: 'loan',
        loadChildren: () => import('./modules/loan/loan.module').then((m) => m.LoanModule)
      },
      {
        path: 'sessions',
        loadChildren: () =>
          import('./modules/sessions/sessions.module').then((m) => m.SessionsModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['view:user:session'],
          checkType: 'AND'
        }
      },
      {
        path: 'notification/:user-id',
        component: PrivateNotificationComponent,
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:create:notification:single_user'],
          checkType: 'AND'
        }
      },
      {
        path: 'notification',
        component: PublicNotificationComponent,
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:create:notification:all_users'],
          checkType: 'AND'
        }
      },
      {
        path: 'asset',
        loadChildren: () => import('./modules/asset/asset.module').then((m) => m.AssetModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:view:wallets:summarize'],
          checkType: 'AND'
        }
      },
      {
        path: 'sliders',
        loadChildren: () =>
          import('./modules/upload-sliders/upload-sliders.module').then(
            (m) => m.UploadSlidersModule
          )
      },
      {
        path: 'currency',
        loadChildren: () =>
          import('./modules/currency/currency.module').then((m) => m.CurrencyModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:view:currency'],
          checkType: 'AND'
        }
      },
      {
        path: 'landing',
        loadChildren: () => import('./modules/landing/landing.module').then((m) => m.LandingModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: [
            'admin:view:landing',
            'admin:create:landing',
            'admin:edit:landing',
            'admin:delete:landing'
          ],
          checkType: 'OR'
        }
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./modules/admin-user/admin-user.module').then((m) => m.AdminUserModule),
        canLoad: [ScopeGuard],
        data: {
          scopes: ['admin:create:admin'],
          checkType: 'AND'
        }
      }
    ]
  },
  {
    path: 'sign-in',
    component: AuthenticationComponent,
    data: {
      action: 'sign-in'
    }
  },
  {
    path: 'two-factor-authentication',
    component: AuthenticationComponent,
    data: {
      action: 'two-factor-authentication'
    }
  },

  {
    path: '**',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class FeatureRoutingModule {
}
